import DialogBox from "components/shared/modal/DialogBox";
import {
  Box,
  FormControlLabel,
  IconButton,
  Switch,
  Tooltip,
  Typography,
} from "@mui/material";
import { Trips } from "@screens/create-new-trip/types/tripTypes";
import { Close } from "@mui/icons-material";
import { useEffect, useState } from "react";
import IconTextButton from "components/shared/buttons/IconTextButton";
import CustomTab from "components/shared/Tab/CustomTab";
import Slots from "./Slots";
import ImageSection from "./ImageSection";
import TripDetails from "./TripDetails";
import TripSections from "screens/create-new-trip/components/TripSections";
import useTripForm from "../formik/useTrip";
import TripFaqs from "screens/create-new-trip/components/TripFaqs";
import { SlotsFormValues } from "../types/types";
import { toast } from "react-toastify";
import { getSlots, updateTrips } from "services/createTripService";
import WarningModal from "components/shared/modal/discard-modal";
import StayDetailsModal from "./StayDetailsSection";
import TripVideoDetails from "screens/create-new-trip/components/TripVideoDetails";
import Inclusion from "screens/create-new-trip/components/Inclusion";
import Exclusion from "screens/create-new-trip/components/Exclusion";
import TTCarryList from "screens/create-new-trip/components/ThingToCarry";

interface TripDetailsModalProps {
  open: boolean;
  handleClose: () => void;
  tripData: Trips;
  handleRefetch: () => void;
}

interface Image {
  file?: Blob | MediaSource | ArrayBuffer;
  preview: string;
  fileName?: string;
  mimetype?: string;
}

interface NightDetails {
  id: string;
  sameAs?: string;
  accomodationType: string;
  hotelName: string;
  district: string;
  state: string;
  amities: string[];
  hotelIntro: string;
  hotelImage: Image[];
}

const TripDetailsModal = (props: TripDetailsModalProps) => {
  const { open, handleClose, tripData, handleRefetch } = props;
  const [activeTab, setActiveTab] = useState(0);
  const { formik } = useTripForm(tripData, tripData?.id, handleRefetch);
  const [edit, setEdit] = useState(false);
  const [hasEdited, setHasEdited] = useState(false);
  const [slots, setSlots] = useState<SlotsFormValues[]>([]);
  const [warningModal, setWarningModal] = useState(false);
  const { noStayDetails, stayDetails, nights } = formik.values;

  const [stayDetaile, setStayDetaile] = useState<NightDetails[]>([]);
  const [notFinalized, setNotFinalized] = useState<boolean>(false);
  const [staySaving, setStaySaving] = useState(false);

  const handleEdit = () => {
    setEdit(!edit);
    if (!hasEdited) {
      setHasEdited(true); // Set true only when toggled first time
    }
  };

  const fetchSlots = async () => {
    try {
      const response = await getSlots(tripData?.id);
      if (response?.status === 200) {
        setSlots(response.data);
      } else {
        toast.error("Failed to fetch slots");
      }
    } catch (error) {
      console.error("Failed to fetch slots", error);
    }
  };
  useEffect(() => {
    fetchSlots();
    setActiveTab(0);
    setEdit(false);
    setHasEdited(false);

    setStayDetaile((stayDetails as NightDetails[]) || []);
    setNotFinalized(noStayDetails || false);
  }, [tripData.id]);

  useEffect(() => {
    setStayDetaile((stayDetails as NightDetails[]) || []);
    setNotFinalized(noStayDetails || false);
  }, [stayDetails, noStayDetails]);

  const handleStaySave = async (
    updatedStayDetails: NightDetails[],
    isNotFinalized: boolean
  ) => {
    if (staySaving) return; 

    setStaySaving(true); 

    try {
      if (!isNotFinalized && updatedStayDetails?.length !== Number(nights)) {
        toast.warning("Please fill stay details for each night.");
        return;
      }

      formik.setFieldValue(
        "stayDetails",
        isNotFinalized ? [] : updatedStayDetails
      );
      formik.setFieldValue("noStayDetails", isNotFinalized);

      const errors = await formik.validateForm();
      if (Object.keys(errors).length > 0) {
        Object.keys(errors).forEach((field) => {
          toast.error(`${field} is incomplete`);
        });
        return;
      }

      await new Promise((res) => setTimeout(res, 0)); 
      formik.handleSubmit(); 
    } finally {
      setStaySaving(false); 
    }
  };

  const slotRefresh = () => {
    fetchSlots();
  };

  const tabs = [
    {
      label: "Trip details",
      content: <TripDetails formik={formik} editable={edit} />,
    },
    {
      label: "Description and FAQs",
      content: (
        <Box>
          <TripSections formik={formik} editable={edit} />
          <Inclusion formik={formik} editable={edit} />
          <Exclusion formik={formik} editable={edit} />
          <TTCarryList formik={formik} editable={edit} />
          <TripFaqs formik={formik} editable={edit} />
          <TripVideoDetails formik={formik} editable={edit} />
        </Box>
      ),
    },
    {
      label: "Images",
      content: (
        <ImageSection
          slots={tripData?.slotCount}
          imageData={tripData?.images ?? null}
          tripId={tripData?.id}
          folderName={`${tripData.searchIdKey}${tripData.searchId}`}
          editable={edit}
          refetch={handleRefetch}
        />
      ),
    },
    {
      label: "Slots",
      content: (
        <Slots
          tripId={tripData?.id}
          merchantId={tripData?.merchantId}
          slots={slots}
          setSlots={setSlots}
          editable={edit}
          imageData={tripData?.images ?? null}
          tripDisplayId={tripData?.searchIdKey + tripData?.searchId}
          tripDisplayTitle={tripData.title}
          refetch={slotRefresh}
        />
      ),
    },
    {
      label: "Stay Details",
      content: (
        <StayDetailsModal
          key={`stay-details-${tripData?.id}`}
          nights={tripData?.nights as number}
          isNotFinalized={notFinalized}
          stayDetails={stayDetaile}
          setStay={setStayDetaile}
          setNoStay={setNotFinalized}
          folderName={`${tripData.searchIdKey}${tripData.searchId}`}
          onSave={(stayDetails, isNotFinalized) =>
            handleStaySave(stayDetails, isNotFinalized)
          }
          editable={edit}
          saving={staySaving}
        />
      ),
    },
  ];

  const handleWaringModal = () => {
    if (hasEdited) {
      setWarningModal(true);
    } else {
      handleClose();
    }
  };

  const handleWarningModalClose = () => {
    setWarningModal(false);
    handleClose();
  };

  const handleSubmit = async () => {
    if (!notFinalized && stayDetaile?.length !== Number(nights)) {
      toast.warning("Please complete and save the stay details.");
      return;
    }
    const errors = await formik.validateForm();

    if (Object.keys(errors).length > 0) {
      Object.keys(errors).forEach((field) => {
        toast.error(`${field} is incomplete`);
      });
      return;
    }

    formik.handleSubmit();
  };

  const handleCloseTrip = async () => {
    if (edit) {
      try {
        const data = {
          tripApprovalStatus: "Closed",
        };
        const response = await updateTrips(tripData?.id, data);
        if (response.message === "Item updated successfully") {
          toast.success("Trip Status updated successfully");
          handleClose();
          handleRefetch();
        }
      } catch (error) {
        console.error("Error closing trip:", error);
      }
    }
  };

  const isVisible = (index: number) => {
    return index === 2 || index === 3 || index === 4;
  };
  return (
    <DialogBox open={open} onClose={handleWaringModal}>
      <Box p={4}>
        <Box display={"flex"} justifyContent={"flex-start"}>
          <Typography variant="h6" fontWeight="600" color="black">
            Trip details
          </Typography>

          <FormControlLabel
            sx={{
              color: "black",
              marginLeft: "auto",
              border: "1px solid #e0e0e0",
              borderRadius: "20px",
              px: 1,
            }}
            control={<Switch checked={edit} onChange={handleEdit} />}
            label="Edit"
          />

          <IconButton
            sx={{ color: "black" }}
            aria-label="close"
            size="small"
            onClick={handleWaringModal}
          >
            <Tooltip title="Close">
              <Close />
            </Tooltip>
          </IconButton>
        </Box>
        <CustomTab
          tabs={tabs}
          activeTab={activeTab}
          onChange={(index) => setActiveTab(index)}
          sx={{
            mt: 2,
            borderBottom: "1px solid #e0e0e0",
            maxHeight: "500px",
          }}
        />
        <Box
          my={4}
          sx={{
            display: isVisible(activeTab) ? "none" : "flex",
            justifyContent: "space-between",
          }}
          gap={2}
        >
          {!(tripData?.tripApprovalStatus === "Closed") && (
            <IconTextButton
              variant="outlined"
              label="Close Trip"
              onClick={handleCloseTrip}
              disabled={formik.isSubmitting}
            />
          )}
          <IconTextButton
            variant="outlined"
            label="Submit Details"
            sx={{ ml: "auto" }}
            onClick={handleSubmit}
            disabled={formik.isSubmitting}
          />
        </Box>
      </Box>
      <WarningModal
        open={warningModal}
        onClose={() => setWarningModal(false)}
        onProceed={handleWarningModalClose}
      />
    </DialogBox>
  );
};
export default TripDetailsModal;
